import { useEffect, useState } from 'react';

import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconPencil, IconPlus, IconTrash } from '@tabler/icons-react';

import { CapabilityEditorModal, LogoPaper } from '@/components';
import { useScopeComponentCapabilities } from '@/hooks';
import { vnextApiClient } from '@/services/vnext-api';
import {
  DataRequirement,
  DimensionCondition,
  ScopeComponentCapability,
  ScopeComponentCapabilityCreateRequest,
  ScopeComponentCapabilityUpdateRequest,
} from '@/types';

interface CapabilitiesSectionProps {
  componentId: string;
}

function describeRequirement(req: DataRequirement): string {
  switch (req.type) {
    case 'api':
      return `API · ${req.provider} · ${req.type_of_check}`;
    case 'human':
      return `Manual${req.description ? ` · ${req.description}` : ''}`;
    case 'metadata':
      return `Metadata${req.field ? ` · ${req.field}` : ''}`;
    case 'scope_component':
      return 'Synthesis';
  }
}

function describeDimension(label: string, cond: DimensionCondition | null): string | null {
  if (!cond || cond.mode === 'all') {
    return null;
  }
  return `${label} ${cond.mode} ${cond.values.join(', ')}`;
}

export function CapabilitiesSection({ componentId }: Readonly<CapabilitiesSectionProps>) {
  const {
    capabilities,
    isLoading,
    fetchCapabilities,
    createCapability,
    updateCapability,
    deleteCapability,
  } = useScopeComponentCapabilities({ componentId });

  const [editorOpened, editorHandlers] = useDisclosure(false);
  const [editTarget, setEditTarget] = useState<ScopeComponentCapability | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ScopeComponentCapability | null>(null);
  const [deleteOpened, deleteHandlers] = useDisclosure(false);
  const [providers, setProviders] = useState<string[]>([]);
  const [typeOfChecks, setTypeOfChecks] = useState<string[]>([]);
  const [providerChecks, setProviderChecks] = useState<Record<string, string[]>>({});

  useEffect(() => {
    vnextApiClient
      .getDataRequirementEnums()
      .then((enums) => {
        setProviders(enums.providers);
        setTypeOfChecks(enums.type_of_checks);
        setProviderChecks(enums.provider_checks);
      })
      .catch(() => {
        // Best-effort; the editor still functions but provider/check selects
        // will be empty.
      });
  }, []);

  const openCreate = () => {
    setEditTarget(null);
    editorHandlers.open();
  };

  const openEdit = (cap: ScopeComponentCapability) => {
    setEditTarget(cap);
    editorHandlers.open();
  };

  const handleSubmit = async (
    data: ScopeComponentCapabilityCreateRequest | ScopeComponentCapabilityUpdateRequest
  ) => {
    if (editTarget) {
      await updateCapability(editTarget.id, data as ScopeComponentCapabilityUpdateRequest);
    } else {
      await createCapability(data as ScopeComponentCapabilityCreateRequest);
    }
    await fetchCapabilities();
  };

  const requestDelete = (cap: ScopeComponentCapability) => {
    setDeleteTarget(cap);
    deleteHandlers.open();
  };

  const confirmDelete = async () => {
    if (!deleteTarget) {
      return;
    }
    await deleteCapability(deleteTarget.id);
    deleteHandlers.close();
    setDeleteTarget(null);
  };

  return (
    <LogoPaper>
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Stack gap={2}>
            <Title order={4}>Capabilities</Title>
            <Text size="sm" c="dimmed">
              Each capability declares one or more data requirements and the jurisdictional
              conditions under which they apply.
            </Text>
          </Stack>
          <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
            Add capability
          </Button>
        </Group>

        {isLoading ? (
          <Group justify="center" py="md">
            <Loader />
          </Group>
        ) : capabilities.length === 0 ? (
          <Text c="dimmed" ta="center" py="md">
            No capabilities yet. Click &ldquo;Add capability&rdquo; to define one.
          </Text>
        ) : (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Data requirements</Table.Th>
                <Table.Th>Conditions</Table.Th>
                <Table.Th w={100} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {capabilities.map((cap) => {
                const conditions = [
                  describeDimension('Jurisdictions', cap.jurisdictions),
                  describeDimension('Subjurisdictions', cap.subjurisdictions),
                ].filter((c): c is string => c !== null);
                return (
                  <Table.Tr key={cap.id}>
                    <Table.Td>
                      <Stack gap={4}>
                        {cap.dataRequirements.map((req, i) => (
                          <Badge key={i} variant="light" size="sm">
                            {describeRequirement(req)}
                          </Badge>
                        ))}
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      {conditions.length === 0 ? (
                        <Text size="sm" c="dimmed">
                          All
                        </Text>
                      ) : (
                        <Stack gap={2}>
                          {conditions.map((c) => (
                            <Text size="sm" key={c}>
                              {c}
                            </Text>
                          ))}
                        </Stack>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4} justify="flex-end">
                        <ActionIcon
                          variant="subtle"
                          aria-label="Edit capability"
                          onClick={() => openEdit(cap)}
                        >
                          <IconPencil size={16} />
                        </ActionIcon>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          aria-label="Delete capability"
                          onClick={() => requestDelete(cap)}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        )}

        <CapabilityEditorModal
          opened={editorOpened}
          onClose={editorHandlers.close}
          capability={editTarget}
          providers={providers}
          typeOfChecks={typeOfChecks}
          providerChecks={providerChecks}
          onSubmit={handleSubmit}
        />

        <Modal
          opened={deleteOpened}
          onClose={() => {
            deleteHandlers.close();
            setDeleteTarget(null);
          }}
          title="Delete capability"
          size="sm"
        >
          <Stack>
            <Text>
              Delete this capability? It can be re-created later but the current configuration will
              be lost.
            </Text>
            <Group justify="flex-end">
              <Button
                variant="default"
                onClick={() => {
                  deleteHandlers.close();
                  setDeleteTarget(null);
                }}
              >
                Cancel
              </Button>
              <Button color="red" onClick={confirmDelete}>
                Delete
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Stack>
    </LogoPaper>
  );
}
